/**
 * useStaleBuildCheck (DNKADM-0372): does the server serve a newer build than the
 * one this tab is running? Mounted once from the authenticated shell.
 *
 * Cadence — never on first mount (a fresh load is current by definition); then
 * on a 10-minute interval and whenever the tab becomes visible again, which is
 * exactly the moment a front-desk iPad comes back from sleep.
 *
 * Result — `stale` flips true when the served identifier differs from the
 * compiled-in one. Nothing here reloads: the Operator ruled "prompt, never
 * reload by itself", so the caller renders a non-blocking notice and the
 * operator decides. `dismiss()` hides it and holds it for 30 minutes — this hook's
 * own checks during the hold are skipped entirely — after which the next check may
 * bring it back. Nagging a busy front desk is how the notice gets ignored.
 *
 * DNKADM-0382 U3 — the fetch and the comparison are no longer this hook's own: it
 * schedules {@link checkBuildCurrency} and reads the shared snapshot, so the Help menu's
 * on-demand check and this timer can never disagree. The dismiss hold stays HERE: it is
 * this notice's policy, not a fact about the build. A check the menu runs inside the hold
 * publishes to the shared snapshot but does not re-raise the notice — the notice shows a
 * `stale` result only when it was established at or after the end of the hold.
 *
 * Disabled outright when the build is unidentifiable (`getBuildId()` is null):
 * no interval, no listener, no fetch. A failed check does nothing at all.
 */
import { useEffect, useRef, useState } from 'react';
import { getBuildId } from '@/config/buildId';
import { checkBuildCurrency, useBuildCheckSnapshot } from './buildCheck';

/** How often the served identifier is re-read while the tab stays open. */
export const STALE_BUILD_CHECK_INTERVAL_MS = 10 * 60 * 1000;
/** How long a dismissed notice stays away before a later check may re-raise it. */
export const STALE_BUILD_DISMISS_HOLD_MS = 30 * 60 * 1000;

export interface StaleBuildCheck {
  /** True when the server currently serves a different build than this tab runs. */
  stale: boolean;
  /** Hide the notice and hold it for {@link STALE_BUILD_DISMISS_HOLD_MS}. */
  dismiss: () => void;
}

export function useStaleBuildCheck(): StaleBuildCheck {
  const snapshot = useBuildCheckSnapshot();
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  // The interval callback reads the hold through a ref so it never closes over a stale value.
  const dismissedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (getBuildId() === null) return;

    let disposed = false;

    const check = () => {
      if (disposed) return;
      const heldSince = dismissedAtRef.current;
      if (heldSince !== null && Date.now() - heldSince < STALE_BUILD_DISMISS_HOLD_MS) return;
      void checkBuildCurrency();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    const timer = setInterval(check, STALE_BUILD_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      disposed = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const dismiss = () => {
    const now = Date.now();
    dismissedAtRef.current = now;
    setDismissedAt(now);
  };

  // Stale, and not inside a dismiss hold: a result established at or after the hold's
  // end shows; one established before or during it (including by the Help menu) does not.
  const stale =
    snapshot.result === 'stale' &&
    snapshot.checkedAt !== null &&
    (dismissedAt === null || snapshot.checkedAt >= dismissedAt + STALE_BUILD_DISMISS_HOLD_MS);

  return { stale, dismiss };
}
